import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Typography, CircularProgress, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../api';

function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [success, setSuccess] = useState(false);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyAndRefresh = async () => {
      console.log('[SUBSCRIPTION SUCCESS] Page loaded with session_id:', sessionId);
      
      // In local development, Stripe webhooks don't fire, so manually upgrade the user
      const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      
      if (isLocal) {
        // Get the plan from localStorage (set before redirecting to Stripe)
        const plan = localStorage.getItem('pendingUpgradePlan') || 'pro';
        console.log('[SUBSCRIPTION SUCCESS] Local mode detected - calling test upgrade endpoint with plan:', plan);
        try {
          await api.post('/auth/test-upgrade/', { plan });
          console.log('[SUBSCRIPTION SUCCESS] Test upgrade successful');
          // Clear the pending plan
          localStorage.removeItem('pendingUpgradePlan');
          // Give extra time for database transaction to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (err) {
          console.error('[SUBSCRIPTION SUCCESS] Test upgrade failed:', err);
        }
      }
      
      // Wait a moment for webhook to process (or test upgrade to complete)
      await new Promise(resolve => setTimeout(resolve, isLocal ? 1500 : 2000));
      
      try {
        console.log('[SUBSCRIPTION SUCCESS] Fetching updated subscription status...');
        const response = await api.get('/auth/subscription/');
        console.log('[SUBSCRIPTION SUCCESS] Updated status:', response.data);
        
        // Check if upgrade was successful
        if (response.data.plan === 'free') {
          console.warn('[SUBSCRIPTION SUCCESS] Still on free plan, retrying in 2s...');
          // Retry once after 2 more seconds
          await new Promise(resolve => setTimeout(resolve, 2000));
          const retryResponse = await api.get('/auth/subscription/');
          console.log('[SUBSCRIPTION SUCCESS] Retry status:', retryResponse.data);
        }
        
        // Update localStorage with new subscription info
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        userInfo.subscription = response.data.plan;
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        
        setLoading(false);
        setSuccess(true);
        
        // Redirect to profile after 3 seconds
        setTimeout(() => {
          console.log('[SUBSCRIPTION SUCCESS] Redirecting to profile...');
          navigate('/profile?payment=success');
        }, 3000);
      } catch (err) {
        console.error('[SUBSCRIPTION SUCCESS] Error fetching status:', err);
        // Don't show error - just redirect anyway since upgrade likely worked
        console.log('[SUBSCRIPTION SUCCESS] Ignoring error and redirecting...');
        setLoading(false);
        setSuccess(true);
        setTimeout(() => {
          navigate('/profile?payment=success');
        }, 2000);
      }
    };

    if (sessionId) {
      verifyAndRefresh();
    } else {
      console.error('[SUBSCRIPTION SUCCESS] No session_id found in URL');
      // No session ID means they navigated here directly - just redirect
      setLoading(false);
      setSuccess(true);
      setTimeout(() => {
        navigate('/profile');
      }, 1000);
    }
  }, [sessionId, navigate]);

  // Get plan name for display
  const planName = localStorage.getItem('pendingUpgradePlan') === 'enterprise' ? 'Enterprise' : 'Pro';

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: '#667eea', mb: 3 }} />
        <Typography variant="h5" sx={{ mb: 2 }}>
          Processing your subscription...
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we confirm your payment
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
        🎉 Welcome to {planName}!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Your subscription is now active. You'll be redirected to your profile in a few seconds...
      </Typography>
      <Button 
        variant="outlined" 
        onClick={() => navigate('/profile?payment=success')}
        sx={{ borderColor: '#667eea', color: '#667eea' }}
      >
        Go to Profile Now
      </Button>
    </Container>
  );
}

export default SubscriptionSuccess;
